import {Box} from "@mui/material";
import {ReactBitsOrb} from "./ReactBitsOrb";
import {useAudioLevel} from "./useAudioLevel";

// Hue-rotation offsets (degrees) solved against ReactBitsOrb's baked-in palette — its
// `baseColor1` sits at ~268.6°, so `target hue - 268.6` rotates that palette onto a specific
// target color. Kept as named offsets rather than recomputed at runtime since they're a fixed
// property of the shader's constants, not something that changes per render.
//   brand purple (#6258E8, ~244°) -> -24
//   gold        (~40°)            -> 132
//   teal        (~194°)           -> -75
//   red         (~0°)             -> 91
const HUE = {
  brand: -24,
  gold: 132,
  teal: -75,
  red: 91,
};

// Per-state look: color (via HUE) plus how "awake" the distortion animation should be.
// idle/listening stay on-brand; thinking/speaking/error borrow the same color language the
// rest of the app already uses for those states (warm = working, cool = responding, red = error).
const STATE_LOOK = {
  idle: {hue: HUE.brand, hoverIntensity: 0.16, forceHoverState: false},
  listening: {hue: HUE.brand, hoverIntensity: 0.42, forceHoverState: true},
  thinking: {hue: HUE.gold, hoverIntensity: 0.3, forceHoverState: true},
  speaking: {hue: HUE.teal, hoverIntensity: 0.5, forceHoverState: true},
  error: {hue: HUE.red, hoverIntensity: 0.26, forceHoverState: false},
};

// `active={false}` mutes the orb to a calm, mostly-still look regardless of `state` — same
// "not started yet" intent as the orb-ui-based VoiceOrb's `active` prop.
const MUTED_LOOK = {hoverIntensity: 0.1, forceHoverState: false};

// Exposed so other spots that want the same color language without a whole orb — e.g. the
// ambient background orb VoiceAssistantScreen renders behind its content — can stay in sync
// with the focal orb's palette instead of hand-picking a separate hue.
export function hueForState(state) {
  return (STATE_LOOK[state] || STATE_LOOK.idle).hue;
}

/**
 * Alternate VoiceOrb built on React Bits' `Orb` (https://reactbits.dev) — a raymarched-feeling
 * WebGL plasma sphere (ogl) — instead of orb-ui. Kept the same external API as the original
 * `VoiceOrb` (`state`, `audioStream`, `audioElement`, `active`, `size`, `onStart`, `className`,
 * `style`) so it's a drop-in swap at any call site; the original VoiceOrb is untouched and still
 * available for side-by-side comparison.
 *
 * `backgroundColor` should roughly match whatever's actually behind this component (the orb's
 * own shader blends its glow against it — see ReactBitsOrb) — defaults to this app's dark
 * modal backdrop; pass a light value when placing it over a light page background.
 */
export function VoiceOrbGlow({
  state = "listening",
  audioStream = null,
  audioElement = null,
  active = true,
  size = 230,
  backgroundColor = "#0c0b16",
  // Keeps the orb on the landing page's brand purple no matter what `state` is, instead of
  // drifting to gold/thinking, teal/speaking, red/error — VoiceAssistantScreen sets this so the
  // modal reads as the same visual identity as the landing page rather than a mood ring.
  lockToBrandHue = false,
  onStart,
  className,
  style,
  ...props
}) {
  const micLevelRef = useAudioLevel(state === "listening" ? audioStream : null);
  const ttsLevelRef = useAudioLevel(state === "speaking" ? audioElement : null);
  const levelRef = state === "speaking" ? ttsLevelRef : micLevelRef;

  const look = STATE_LOOK[state] || STATE_LOOK.idle;
  const hue = lockToBrandHue ? HUE.brand : look.hue;
  const {hoverIntensity, forceHoverState} = active ? look : {...look, ...MUTED_LOOK};

  return (
    <Box
      className={className}
      onClick={onStart}
      role={onStart ? "button" : undefined}
      tabIndex={onStart ? 0 : undefined}
      aria-label={onStart ? "Start voice call" : `AI voice orb, ${state}`}
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        cursor: onStart ? "pointer" : "default",
        ...style,
      }}
    >
      <ReactBitsOrb
        hue={hue}
        hoverIntensity={hoverIntensity}
        forceHoverState={forceHoverState}
        rotateOnHover
        backgroundColor={backgroundColor}
        levelRef={active ? levelRef : null}
        {...props}
      />
    </Box>
  );
}
